import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import { OPERATING_MODEL_LABELS, operatingModelsFor, type OperatingModel } from '../../../services/admin'
import { useSector } from '../../../sector/sector'

/** Step 1: create the customer, in the sector the console is working in. */
export default function CreateTenantStep({ onSuccess }: { onSuccess: (tenantId: string) => void }) {
  const sector = useSector()
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    email: '',
    phone: '',
    address: '',
  })

  // How they run: a landlord or an agency, for rental. Only asked where the
  // sector has a choice; an agency is never assumed (it turns on trust accounting).
  const models = operatingModelsFor(sector.code)
  const [model, setModel] = useState<OperatingModel | null>(models.length === 1 ? models[0] : null)
  const nameLabel =
    sector.code === 'school' ? 'School name'
      : model === 'agency_managed' ? 'Agency name'
        : model === 'owner_managed' ? "Owner's name (person or company)"
          : 'Name'

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/admin/wizard/create-tenant', { ...formData, vertical_id: sector.id, operating_model: model })
      return res.data
    },
    onSuccess: (data) => {
      toast.success('Customer created!')
      onSuccess(data.tenant_id)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to create customer'))
    },
  })

  return (
    <div>
      <h2 className="text-xl font-semibold mb-1">Step 1: Create the customer</h2>
      <p className="mb-4 text-body-sm text-ink-muted">A new {sector.name} customer.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          createMutation.mutate()
        }}
        className="space-y-4"
      >
        {models.length > 1 && (
          <fieldset>
            <legend className="block text-sm font-medium text-ink">How do they run? *</legend>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {models.map((m) => (
                <label key={m} className="flex items-start gap-2 rounded-button border border-hairline p-3 cursor-pointer has-[:checked]:border-accent">
                  <input type="radio" name="operating-model" value={m} checked={model === m} onChange={() => setModel(m)} required className="mt-1" />
                  <span className="text-body-sm">
                    <span className="block font-medium text-ink">{OPERATING_MODEL_LABELS[m]}</span>
                    <span className="block text-ink-muted">
                      {m === 'agency_managed'
                        ? 'Collects rent for owners: rent is split to each owner, with trust accounting.'
                        : 'Collects rent for their own properties.'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="ct-name" className="block text-sm font-medium text-ink">{nameLabel} *</label>
            <input
              id="ct-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Short name *</label>
            <input
              type="text"
              required
              value={formData.short_name}
              onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-ink">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          {/* No paybill here: payment details are copied from the connected bank or
              M-PESA account once LipaFlow connects it (payment-integrity P4). */}
        </div>
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={createMutation.isPending || (models.length > 0 && !model)}
            className="px-6 py-2 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Customer'}
          </button>
        </div>
      </form>
    </div>
  )
}
